import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Box,
	Typography,
	Button,
	List,
	ListItem,
	IconButton,
	Divider,
	useTheme,
} from '@mui/material';
import { shades } from '../../../theme';
import {
	removeFromCart,
	increaseCount,
	decreaseCount,
} from '../../redux/reducers/cartSlice';
import { Link, useNavigate } from 'react-router-dom';
import { APP_URLS } from '../../../urls';
import bannerImage from '../../data/about/m-treat-doctor.jpg';
import DeleteIcon from '@mui/icons-material/Delete';
import { currencyMapping } from '../../utils/currencyData';

// import { APP_URLS } from '../../../urls';

const ShoppingCart = () => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';
	const dispatch = useDispatch();
	const cart = useSelector((state) => state.cart.cart);

	const handleRemove = (itemId) => {
		dispatch(removeFromCart({ id: itemId }));
	};

	const handleIncrease = (itemId) => {
		dispatch(increaseCount({ id: itemId }));
	};

	const handleDecrease = (itemId) => {
		dispatch(decreaseCount({ id: itemId }));
	};

	const reduxAuthState = useSelector((state) => state.auth?.isAuthenticated);
	const reduxPatientAuthState = useSelector(
		(state) => state.patientAuth?.isAuthenticated,
	);
	// const isAuthenticated =
	// 	reduxAuthState ||
	// 	reduxPatientAuthState ||
	// 	localStorage.getItem('isAuthenticated') === 'true';
	const navigate = useNavigate();

	const [convertedPrices, setConvertedPrices] = useState({});
	const [conversionLoading, setConversionLoading] = useState({});
	const [convertedTotals, setConvertedTotals] = useState({});
	const [isConverting, setIsConverting] = useState({});
	const [convertedSubtotal, setConvertedSubtotal] = useState(null);
	const [isSubtotalConverting, setIsSubtotalConverting] = useState(false);
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	// Add currency formatting utilities
	const getCurrencySymbol = (currencyCode) => {
		const country = Object.entries(currencyMapping).find(
			([_, data]) => data.code === currencyCode,
		);
		return country ? country[1].symbol : currencyCode;
	};

	const formatPrice = (price, currency) => {
		const symbol = getCurrencySymbol(currency);
		return `${symbol}${price}`;
	};

	// Add conversion function
	const convertPrice = async (itemId, price, currency) => {
		if (!price || !currency) return;

		setConversionLoading((prev) => ({ ...prev, [itemId]: true }));
		try {
			const response = await fetch('/medication/drug/convert_currency/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					price,
					currency,
				}),
			});
			const data = await response.json();
			if (data.converted_price) {
				setConvertedPrices((prev) => ({
					...prev,
					[itemId]: {
						price: data.converted_price,
						currency: data.user_currency,
						symbol: getCurrencySymbol(data.user_currency),
					},
				}));
			}
		} catch (error) {
			console.error('Price conversion failed:', error);
		} finally {
			setConversionLoading((prev) => ({ ...prev, [itemId]: false }));
		}
	};

	// Add useEffect
	useEffect(() => {
		cart.forEach((item) => {
			convertPrice(item.id, item.price, item.currency);
		});
	}, [cart]);

	// Add conversion function
	const convertCartItemTotal = async (itemId, total, currency) => {
		if (!total || !currency) return;

		setIsConverting((prev) => ({ ...prev, [itemId]: true }));
		try {
			const response = await fetch('/medication/drug/convert_currency/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					price: total,
					currency,
				}),
			});
			const data = await response.json();
			if (data.converted_price) {
				setConvertedTotals((prev) => ({
					...prev,
					[itemId]: {
						price: data.converted_price,
						currency: data.user_currency,
					},
				}));
			}
		} catch (error) {
			console.error('Total conversion failed:', error);
		} finally {
			setIsConverting((prev) => ({ ...prev, [itemId]: false }));
		}
	};

	useEffect(() => {
		cart.forEach((item) => {
			const total = item.price * item.count;
			convertCartItemTotal(item.id, total, item.currency);
		});
	}, [cart]);

	// Add helper to calculate subtotal from converted totals
	const getConvertedSubtotal = () => {
		return cart
			.reduce((total, item) => {
				const convertedTotal =
					convertedTotals[item.id]?.price || item.price * item.count;
				return total + parseFloat(convertedTotal);
			}, 0)
			.toFixed(2);
	};

	// Add conversion function for subtotal
	const convertSubtotal = async () => {
		const subtotal = getConvertedSubtotal();
		if (!subtotal || !cart.length) return;

		setIsSubtotalConverting(true);
		try {
			const currency =
				convertedTotals[cart[0].id]?.currency || cart[0].currency;
			setConvertedSubtotal({
				price: subtotal,
				currency,
			});
		} catch (error) {
			console.error('Subtotal conversion failed:', error);
		} finally {
			setIsSubtotalConverting(false);
		}
	};

	// Update useEffect to recalculate subtotal when item totals change
	useEffect(() => {
		if (cart.length > 0) {
			convertSubtotal();
		}
	}, [cart, convertedTotals]);

	// Add total calculation helper
	const getTotal = () => {
		const subtotal = parseFloat(getConvertedSubtotal());
		// const tax = subtotal * 0.16; // 16% tax
		return subtotal.toFixed(2);
	};

	useEffect(() => {
		// Only consider localStorage if Redux also confirms authentication
		const authenticated = reduxAuthState || reduxPatientAuthState;
		setIsAuthenticated(authenticated);
	}, [reduxAuthState, reduxPatientAuthState]);

	const handleProceedToCheckout = () => {
		console.log('Authentication check triggered');

		// Access variables that were defined at component level
		const localStorageAuth = localStorage.getItem('isAuthenticated') === 'true';

		// SAFE: Log authentication state details
		console.log('Authentication details:', {
			'Redux auth state': reduxAuthState,
			'Redux patientAuth state': reduxPatientAuthState,
			'localStorage isAuthenticated': localStorageAuth,
			'User has attempted login': Boolean(
				localStorage.getItem('loginAttempted'),
			),
			'Current auth payload exists': Boolean(
				sessionStorage.getItem('authPayload'),
			),
		});

		console.log('isAuthenticated value:', isAuthenticated);

		if (!isAuthenticated) {
			console.log('User not authenticated, redirecting to login');
			// Store ONLY that we're in checkout process, not credentials
			localStorage.setItem('checkoutInProgress', 'true');
			localStorage.setItem('checkoutTargetStep', '0');
			navigate('/app/user/login');
			return;
		}

		console.log('User authenticated, proceeding to checkout');
		navigate(APP_URLS.checkout);
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				backgroundColor: isDarkMode ? shades.secondary[800] : 'white',
				color: isDarkMode ? shades.white[100] : 'black',
				transition: 'background-color 0.3s ease, color 0.3s ease',
				minHeight: '100vh',
			}}
		>
			<Box
				sx={{
					width: { xs: '95%', sm: '95%', md: '80%' },
					paddingTop: 3,
					mb: 5,
				}}
			>
				{/* Cart Items Section */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'column' },
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'row', md: 'row' },
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							//border: '2px solid',
							width: { xs: '95%', sm: '60%', md: '60%' },
							mb: 4,
							mt: 3,
							ml: 0.5,
						}}
					>
						{/* Left Column: Cart Items */}
						<Box
							sx={{
								marginRight: { sm: '2rem' },
								boxShadow: 3,
								border: '1px solid #ddd',
								//p: 3,
								//mb: 4,
							}}
						>
							{/* Cart Header */}
							<Typography
								variant="h2"
								fontWeight="bold"
								sx={{
									mt: 3,
									mb: 2,
									ml: 3,
									textAlign: 'left',
									borderBottom: '1px solid #ccc',
								}}
							>
								Shopping Cart
							</Typography>
							{cart.length === 0 ? (
								<Typography>No items in the cart.</Typography>
							) : (
								<List>
									{cart.map((item) => (
										<ListItem
											key={item.id}
											sx={{
												display: 'flex',
												flexDirection: { xs: 'column', sm: 'row' },
												alignItems: 'flex-start',
												mb: 0.5,
												pb: 2,
												fontSize: { xs: '0.75rem', sm: '0.8rem', md: '1rem' },
											}}
										>
											{/* Item Image */}
											<Box sx={{ flex: 1 }}>
												<img
													src={item.img}
													alt={item.name}
													style={{
														width: '70px',
														height: '70px',
														objectFit: 'cover',
													}}
												/>
											</Box>

											{/* Item Details */}
											<Box sx={{ flex: 1 }}>
												<Typography
													variant="h6"
													fontWeight="bold"
													sx={{
														fontSize: {
															xs: '0.8rem',
															sm: '0.8rem',
															md: '1rem',
														},
													}}
												>
													{item.name}
												</Typography>
												<Typography
													sx={{
														fontSize: {
															xs: '0.75rem',
															sm: '0.7rem',
															md: '1rem',
														},
													}}
												>
													{conversionLoading[item.id] ? (
														<span>Converting...</span>
													) : (
														<span>
															{formatPrice(
																convertedPrices[item.id]?.price || item.price,
																convertedPrices[item.id]?.currency ||
																	item.currency,
															)}
														</span>
													)}
												</Typography>
											</Box>

											{/* Quantity Field */}
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'row',
													alignItems: 'center',
													justifyContent: 'space-between',
													width: { xs: '95%', sm: '40%', md: '40%' },
													ml: 0,
													mb: 0,
												}}
											>
												{/* Quantity Controls */}
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														border: '1px solid #ddd',
														borderRadius: '8px',
														width: '100px',
														padding: '4px',
													}}
												>
													{/* Decrement Button */}
													<IconButton
														onClick={() => handleDecrease(item.id)}
														color="primary"
														sx={{
															padding: '3px',
															minWidth: '24px', // Smaller icon size
															minHeight: '24px',
															// borderRadius: '50%',
															color: 'black',
															backgroundColor: '#f1f1f1',
															'&:hover': { backgroundColor: '#e0e0e0' },
															fontSize: { xs: '0.8rem', sm: '0.8rem' }, // Smaller font size for icons
														}}
													>
														−
													</IconButton>

													{/* Quantity Display */}
													<Typography
														sx={{
															fontWeight: 'bold',
															fontSize: {
																xs: '0.75rem',
																sm: '0.8rem',
																md: '1rem',
															},
															textAlign: 'center',
															width: '40px',
															borderLeft: '1px solid #ddd',
															borderRight: '1px solid #ddd',
															padding: '3px',
														}}
													>
														{item.count}
													</Typography>

													{/* Increment Button */}
													<IconButton
														onClick={() => handleIncrease(item.id)}
														color="primary"
														sx={{
															padding: '3px',
															minWidth: '24px',
															minHeight: '24px',
															// borderRadius: '50%',
															color: 'black',
															backgroundColor: '#f1f1f1',
															'&:hover': { backgroundColor: '#e0e0e0' },
															fontSize: { xs: '0.8rem', sm: '0.8rem' },
														}}
													>
														+
													</IconButton>
												</Box>

												{/* Item Total Price */}
												<Typography
													sx={{
														fontWeight: 'bold',
														alignItems: 'row',
														color: '#2783f7',
														fontSize: {
															xs: '0.75rem',
															sm: '0.6rem',
															md: '1rem',
														},
														ml: 0.5,
														// textAlign: 'right',
													}}
												>
													{isConverting[item.id] ? (
														<span>Converting...</span>
													) : (
														<span>
															{formatPrice(
																convertedTotals[item.id]?.price ||
																	(item.price * item.count).toFixed(2),
																convertedTotals[item.id]?.currency ||
																	item.currency,
															)}
														</span>
													)}
												</Typography>

												{/* Delete Icon Button */}
												<IconButton
													onClick={() => handleRemove(item.id)}
													color="secondary"
													sx={{
														padding: '6px',
														backgroundColor: 'white',
														'&:hover': { backgroundColor: '#f5c6cb' },
														fontSize: {
															xs: '0.75rem',
															sm: '0.9rem',
															md: '1rem',
														},
														marginLeft: '8px',
													}}
												>
													<DeleteIcon
														sx={{
															fontSize: { xs: '18px', sm: '15px', md: '24px' },
														}}
													/>
												</IconButton>
											</Box>
										</ListItem>
									))}
								</List>
							)}
						</Box>

						<Link to={APP_URLS.products} style={{ textDecoration: 'none' }}>
							<Button
								variant="contained"
								sx={{
									width: { xs: '50%', sm: '50%', md: '40%' },
									fontSize: { xs: '0.6rem', sm: '0.9rem', md: '1rem' },
									color: 'white',
									mt: 2,
									backgroundColor: '#2783f7',
									border: isDarkMode
										? '1px solid rgba(255,255,255,0.2)'
										: 'none',
									'&:hover': {
										backgroundColor: '#1a6ccc',
										color: 'white',
										transition: 'background-color 0.3s ease',
										boxShadow: isDarkMode
											? '0 0 8px rgba(255,255,255,0.2)'
											: '0 4px 8px rgba(0,0,0,0.1)',
									},
								}}
							>
								Continue Shopping
							</Button>
						</Link>
					</Box>

					{/* Right Column: Order Summary and Checkout */}
					<Box
						sx={{
							flex: 1,

							// border: '2px solid',
							mb: 4,
							mt: 3,
							ml: 1,
							width: { xs: '95%', sm: '35%', md: '40%' },
						}}
					>
						<Box
							sx={{
								marginRight: { sm: '2rem' },
								boxShadow: 3,
								border: '1px solid #ddd',
								// p: 3,
								mb: 4,
							}}
						>
							{/* Totals Section */}
							<Divider sx={{ my: 1 }} />
							{/* Cart Summary Section */}
							<Box sx={{ textAlign: 'left', pl: 2 }}>
								<Box sx={{ mb: 3, borderBottom: '1px solid #ccc' }}>
									{/* Loop through cart items for individual item display */}
									{cart.map((item) => (
										<Box key={item.id} sx={{ mb: 0.1 }}>
											{/* Display item count and price */}
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
													fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
													pr: 2,
													pl: 1,
												}}
											>
												<Typography variant="h6" sx={{ fontSize: 'inherit' }}>
													{item.count} {item.name}:
												</Typography>
												<Typography
													variant="h6"
													sx={{
														color: '#2783f7',
														fontSize: 'inherit',
													}}
												>
													{isConverting[item.id] ? (
														<span>Converting...</span>
													) : (
														formatPrice(
															convertedTotals[item.id]?.price ||
																(item.price * item.count).toFixed(2),
															convertedTotals[item.id]?.currency ||
																item.currency,
														)
													)}
												</Typography>
											</Box>
										</Box>
									))}
								</Box>

								<Box sx={{ mb: 3, borderBottom: '1px solid #ccc' }}>
									{/* Subtotal Calculation */}
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
											pr: 2,
											pl: 1,
										}}
									>
										<Typography variant="h6" sx={{ fontSize: 'inherit' }}>
											Subtotal (Tax Excl):
										</Typography>
										<Typography
											variant="h6"
											sx={{
												color: '#2783f7',
												fontSize: 'inherit',
											}}
										>
											{isSubtotalConverting ? (
												<span>Converting...</span>
											) : (
												formatPrice(
													convertedSubtotal?.price || getConvertedSubtotal(),
													convertedSubtotal?.currency ||
														cart[0]?.currency ||
														'USD',
												)
											)}
										</Typography>
									</Box>

									{/* Tax Calculation */}
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
											pr: 2,
											pl: 1,
										}}
									>
										{/* <Typography variant="h6" sx={{ fontSize: 'inherit' }}>
											Tax:
										</Typography>
										<Typography
											variant="h6"
											sx={{
												color: '#2783f7',
												fontSize: 'inherit',
											}}
										>
											{cart.length > 0 ? cart[0].currency : '$'}{' '}
											{calculateTax().toFixed(2)}
										</Typography> */}
									</Box>
								</Box>

								{/* Total Amount */}
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
										pr: 2,
										pl: 1,
										mb: 1,
									}}
								>
									<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
										Total:
									</Typography>
									<Typography
										variant="h6"
										sx={{
											fontWeight: 'bold',
											color: '#2783f7',
										}}
									>
										{isSubtotalConverting ? (
											<span>Converting...</span>
										) : (
											formatPrice(
												getTotal(),
												convertedSubtotal?.currency ||
													cart[0]?.currency ||
													'USD',
											)
										)}
									</Typography>
								</Box>
							</Box>
							{/* Checkout Buttons */}
							<Box
								sx={{
									display: 'flex',
									width: '100%',
									justifyContent: 'center',
									alignItems: 'center',
									borderTop: isDarkMode
										? '1px solid rgba(255,255,255,0.1)'
										: '1px solid #ddd',
									pt: 2,
									pb: 2,
									pl: 4,
									pr: 4,
								}}
							>
								<Button
									variant="contained"
									onClick={handleProceedToCheckout}
									sx={{
										width: '100%',
										maxWidth: { xs: '70%', sm: '100%', md: '80%' },
										fontSize: { xs: '0.7rem', sm: '0.9rem', md: '1rem' },
										color: 'white',
										backgroundColor: '#2783f7',
										whiteSpace: { xs: 'normal', md: 'nowrap' },
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										padding: { xs: '6px 10px', md: '8px 20px' },
										border: isDarkMode
											? '1px solid rgba(255,255,255,0.2)'
											: 'none',
										'&:hover': {
											backgroundColor: '#1a6ccc',
											color: 'white',
											transition: 'background-color 0.3s ease',
											boxShadow: isDarkMode
												? '0 0 8px rgba(255,255,255,0.2)'
												: '0 4px 8px rgba(0,0,0,0.1)',
										},
									}}
								>
									Proceed to Checkout
								</Button>
							</Box>
						</Box>

						<Box
							sx={{
								marginRight: { sm: '2rem' },
								mb: 4,
							}}
						>
							{/* Policy Section */}
							<Box sx={{ mt: 3, gap: 2, p: 2 }}>
								<Typography
									variant="h6"
									fontWeight="bold"
									href="/security-policy"
									sx={{ borderBottom: '1px solid #ccc', paddingBottom: '4px' }}
								>
									Security Policy
								</Typography>
								<Typography
									variant="h6"
									fontWeight="bold"
									href="/delivery-policy"
									sx={{ borderBottom: '1px solid #ccc', paddingBottom: '4px' }}
								>
									Delivery Policy
								</Typography>
								<Typography
									variant="h6"
									fontWeight="bold"
									href="/return-policy"
									sx={{ borderBottom: '1px solid #ccc', paddingBottom: '4px' }}
								>
									Return Policy
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default ShoppingCart;
